/** The linting engine for TSCN files: strict parsing, then the semantic rules. */
import { connectionDiagnostics } from './connectionDiagnostics.js';
import { editableDiagnostics } from './editableDiagnostics.js';
import { orphanDiagnostics } from './orphanDiagnostics.js';
import { danglingResourceDiagnostics } from './danglingResources.js';
import { excludedResourceTypeDiagnostics } from './excludedResourceTypes.js';
import { resourceRuleDiagnostics } from './resourceRules.js';
import { unimportableGltfDiagnostics, usedGltfResources, } from './unimportableGltf.js';
import { GltfVerdicts } from './gltfVerdicts.js';
import { PLUGIN_PROBE_PATHS, ProjectPluginProbes } from './projectPlugins.js';
import { LintSession } from './LintSession.js';
import { sortDiagnostics } from './mergeDiagnostics.js';
import { ruleRegistry } from './RuleRegistry.js';
import { StrictTscnParser } from './StrictTscnParser.js';
import { isLegacyFormat, readHeaderFormat } from './headerFormat.js';
import { FILE_DIAGNOSTICS, STRICT_PARSER_RULE_NAME, formatRuleName } from './fileDiagnostics.js';
import { armDiagnostic } from './ruleArms.js';
import { headingLocation } from './sourceLocation.js';
const NO_GLTF_USES = new Map();
/**
 * `diagnostic` put on `heading`, unless it carries a location of its own. A copy, not a write:
 * a rule may hand back one object for several nodes.
 */
function onHeading(diagnostic, heading) {
    if (diagnostic.location || !heading)
        return diagnostic;
    return { ...diagnostic, location: heading };
}
export class Linter {
    parser = new StrictTscnParser();
    /** What the cross-file rules read, per provider, for the linter's lifetime, so every session shares it. */
    projectReads = {
        verdicts: new GltfVerdicts(),
        plugins: new ProjectPluginProbes(),
    };
    /**
     * Lint TSCN file content in two phases: strict parsing (syntax and format errors), then the semantic rules.
     *
     * @param content - Raw TSCN file content
     * @returns Array of diagnostics (parse errors + rule violations)
     */
    lint(content) {
        return sortDiagnostics(this.lintFile(content).diagnostics);
    }
    /**
     * A session for one document a host lints again on each edit. It adds the rules that read the files the document
     * uses, and keeps their last diagnostics beside the file's own while the next read is pending.
     */
    session() {
        return new LintSession((content, provider) => this.lintProject(content, provider));
    }
    /**
     * `lint`, plus the diagnostics of the rules that read the files `content` uses through `provider`, sorted, in one
     * answer. A file the provider cannot deliver adds no diagnostic.
     *
     * @param content - Raw TSCN file content
     * @param provider - Loads a `res://` path under the file's `res://` root, or null for a file with no root
     */
    async lintComplete(content, provider) {
        const { now, later } = this.session().lint(content, provider);
        return (await later) ?? now;
    }
    /** One strict parse for both the file's own rules and the cross-file ones, as a session assembles them. */
    lintProject(content, provider) {
        const { diagnostics, parsed } = this.lintFile(content);
        const usedGltf = provider && parsed?.scene ? usedGltfResources(parsed.scene) : [];
        if (!provider || !parsed || usedGltf.length === 0) {
            return { local: diagnostics, gltfUses: NO_GLTF_USES, crossFile: null, reads: [] };
        }
        const gltfUses = new Map();
        for (const { resource } of usedGltf) {
            gltfUses.set(resource.id, { path: resource.path, location: headingLocation(parsed.lines, resource) });
        }
        return {
            local: diagnostics,
            gltfUses,
            crossFile: this.gltfDiagnostics(usedGltf, parsed.lines, provider),
            reads: [...new Set(usedGltf.map(({ resource }) => resource.path)), ...PLUGIN_PROBE_PATHS],
        };
    }
    /**
     * The cross-file rule's diagnostics, or its crash as `rule-crashed`. A throw is that rule's own diagnostic, as in
     * `lintNode`: a rejection would reach a host as a failed lint, and the CLI would drop every local diagnostic with it.
     * The crash names no line, since the rule is about the whole file.
     */
    async gltfDiagnostics(used, lines, provider) {
        try {
            return await unimportableGltfDiagnostics(used, lines, provider, this.projectReads);
        }
        catch (error) {
            const reason = error instanceof Error ? error.message : String(error);
            const rule = FILE_DIAGNOSTICS.unimportableGltf.ruleName;
            return [
                armDiagnostic(FILE_DIAGNOSTICS.ruleCrashed, { name: '<unknown>', type: '<unknown>' }, `Rule '${rule}' threw while reading the files this scene uses: ${reason}. ` +
                    'Its own findings are missing; every file-local rule ran.'),
            ];
        }
    }
    /**
     * The unsorted diagnostics of `content` alone, and the strict parse they came from. No parse for a legacy-format
     * file, which is declined whole.
     */
    lintFile(content) {
        const diagnostics = [];
        // Phase 0: the file's own format version. These rules are written against
        // the format Godot writes today, so an older one is declined whole rather
        // than reported against a grammar it predates.
        const legacy = this.legacyFormatDiagnostic(content);
        if (legacy)
            return { diagnostics: [legacy] };
        // Phase 1: strict parsing.
        const parseResult = this.parser.parse(content);
        if (parseResult.errors.length > 0) {
            for (const d of this.convertParseErrors(parseResult.errors))
                diagnostics.push(d);
        }
        // Phase 2: semantic rules, on every scene the strict parser could build.
        // A property error does not withhold the tree, so a file reports its parse
        // errors and the semantic findings underneath them together.
        if (parseResult.scene) {
            const { scene, lines } = parseResult;
            for (const d of orphanDiagnostics(scene))
                diagnostics.push(d);
            for (const d of connectionDiagnostics(scene))
                diagnostics.push(d);
            for (const d of editableDiagnostics(scene))
                diagnostics.push(d);
            for (const d of danglingResourceDiagnostics(scene, lines))
                diagnostics.push(d);
            for (const d of excludedResourceTypeDiagnostics(scene, lines))
                diagnostics.push(d);
            for (const d of resourceRuleDiagnostics(scene, lines))
                diagnostics.push(d);
            for (const d of this.lintScene(scene, lines))
                diagnostics.push(d);
        }
        return { diagnostics, parsed: parseResult };
    }
    /**
     * The one diagnostic a pre-current-format file gets, or `null` for every other file. Version 3 gave ext/subresources
     * string ids (`resource_format_text.h:44`), so on a `format=2` file every other diagnostic would be wrong, not noisy.
     * It does not claim the file is invalid: the engine loads it, with no less-than comparison against the format version in
     * `resource_format_text.cpp`. So it is a claim about the linter's scope, reported at `info`, and it names the suppression.
     */
    legacyFormatDiagnostic(content) {
        const header = readHeaderFormat(content);
        if (!header || !isLegacyFormat(header.format))
            return null;
        return armDiagnostic(FILE_DIAGNOSTICS.legacyFormat, { name: '<unknown>', type: '<unknown>' }, 
        // "3 or 4" rather than one number: one 4.6.3 saver writes both, choosing
        // per file (`resource_format_text.cpp:1798`).
        `Header format=${header.format} predates the text format Godot writes today (3 or 4). ` +
            'Lint rules target the current format, so nothing else in this file is reported. ' +
            'Open and re-save the file in Godot to migrate it.', { line: header.line, column: 1 });
    }
    /**
     * Parse errors as diagnostics. The owner is the section the scanning loop was inside ({@link ParseError.nodeName}),
     * so a refused property value names the node that wrote it. A `[sub_resource]` body is named by its `id=`, since
     * several shapes of one type sit side by side. `<unknown>` is the true owner of a malformed heading, the `format=`
     * header and a `.tres`'s `[resource]` body, which no id identifies. A validator's refusal is named after its property,
     * and an error no property owns is `strict-parser`.
     */
    convertParseErrors(errors) {
        return errors.map((error) => ({
            severity: error.severity,
            message: error.message,
            nodeName: error.nodeName ?? '<unknown>',
            nodeType: error.nodeType ?? '<unknown>',
            ruleName: error.propertyKey === undefined ? STRICT_PARSER_RULE_NAME : formatRuleName(error.propertyKey),
            location: {
                line: error.line,
                column: error.column,
            },
        }));
    }
    /**
     * Run the semantic rules over a parsed TSCN scene.
     * @param scene - The parsed scene to validate
     * @param lines - Where each node of `scene` sits, for the heading each diagnostic is put on
     * @returns Array of diagnostics found
     */
    lintScene(scene, lines) {
        const diagnostics = [];
        for (const node of scene.nodes) {
            this.lintNode(scene, node, lines, diagnostics);
        }
        return diagnostics;
    }
    /**
     * Lint a node and, recursively, its children. Every diagnostic it reports is about `node`
     * (`LintRule.check`), so one without a `location` goes on the node's heading.
     */
    lintNode(scene, node, lines, diagnostics) {
        const rules = ruleRegistry.getRulesForNodeType(node.type);
        const heading = headingLocation(lines, node);
        const context = { scene, node };
        for (const rule of rules) {
            // Appended one at a time: an indexed-family rule reports per index, and spreading 130,000 arguments
            // exceeds the call limit. A throw in one rule is that rule's own diagnostic and the walk goes on:
            // no host catches around `lint`, so an uncaught throw drops every diagnostic of the file.
            try {
                for (const diagnostic of rule.check(context))
                    diagnostics.push(onHeading(diagnostic, heading));
            }
            catch (error) {
                const reason = error instanceof Error ? error.message : String(error);
                diagnostics.push(armDiagnostic(FILE_DIAGNOSTICS.ruleCrashed, node, `Rule '${rule.meta.name}' threw while linting '${node.name}': ${reason}. ` +
                    'Its own findings for this node are missing; every other rule ran.', heading));
            }
        }
        for (const child of node.children) {
            this.lintNode(scene, child, lines, diagnostics);
        }
    }
}
//# sourceMappingURL=Linter.js.map